/**
 * Mock-exam problem block renderer.
 */

import { Divider } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';

import { useAsterController } from "context";
import { VButton } from "form";
import { getModuleIdFromCourseId, getQuestionMapFromIndex } from "utils/grade.util";
import ChoiceResponseRender from "./ChoiceResponseRender";
import EquationRender from "./EquationRender";
import MultipleChoiceResponseRender from "./MultipleChoiceResponseRender";
import NumericalResponseRender from "./NumericalResponseRender";
import OptionResponseRender from "./OptionResponseRender";
import PRender from "./PRender";
import SolutionRender from "./SolutionRender";
import StringResponseRender from "./StringResponseRender";

/**
 * Parses mock-exam OLX, grades locally, and optionally posts answers.
 *
 * @param {{ html: string, id: string, onSubmit?: Function, postFlag?: string }} props
 */
const ProblemMock = (props) => {
  const { rawText, grade, courseId, blockId, onSubmitProblem, onContinueClick } = props; // Problem block and it's grade

  const { t } = useTranslation('common');

  const [controller] = useAsterController();
  const { user } = controller;

  const [tags, setTags] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [isShowHint, setShowHint] = useState(false);
  const [isContainHint, setContainHint] = useState(false);
  const [isShowMark, setShowMark] = useState(false);
  const [isSubmited, setIsSubmited] = useState(false);
  const [touchedQuizes, setTouchedQuizes] = useState([]);

  useEffect(() => {
    if (tags && tags.length) {
      const gradedIds = tags.filter(t => t.grade !== null).map(m => m.grade['id']);
      setTouchedQuizes(gradedIds.map(g => ({ 'id': g, 'state': false })));
    }
  }, [tags]);

  useEffect(() => {
    const parser = new DOMParser();
    const xml = parser.parseFromString(rawText, 'text/xml');
    const questionList = ['optionresponse', 'multiplechoiceresponse', 'choiceresponse', 'numericalresponse', 'stringresponse'];
    const items = xml.querySelector('problem')?.children || [];

    // Map tag to its corresponding grade and set state
    let mappedItems = [];
    let qIndex = 0;
    for (let i = 0; i < items.length; i++) {
      const tagName = items[i].tagName ? items[i].tagName.toLowerCase() : null;
      if (!tagName) continue;

      // if there is solution tag, then, enable show hint button
      if (tagName === 'solution') setContainHint(true);

      // Map grade to its corresponding question tag ignoring other tags
      if (questionList.indexOf(tagName) > -1) {
        if (!grade || !grade.state || !grade.state.question_map) {
          // If there is no grade for this problem block, then create new one
          mappedItems.push({
            key: i,
            tag: items[i],
            grade: {
              id: `${blockId}_${qIndex + 2}_1`,
              correctness: null,
              student_answer: null
            }
          });
        } else {
          mappedItems.push({ key: i, tag: items[i], grade: getQuestionMapFromIndex(grade.state?.question_map, qIndex) });
        }
        qIndex++;
      } else {
        mappedItems.push({ key: i, tag: items[i], grade: null });
      }
    }

    setTags(mappedItems);

    // Set answer state
    setAnswers(grade && grade.state && grade.state.question_map ? grade.state.question_map : []);

  }, [rawText, grade, blockId]);

  /**
   * Upserts a mock-exam answer in local state.
   *
   * @param {Object} ans Answer payload.
   * @returns {void}
   */
  const onSetAnswer = (ans) => {
    setTouchedQuizes(quizes => {
      const qIdx = quizes.findIndex(q => q.id === ans.id);
      if (qIdx >= 0) quizes[qIdx]['state'] = true;
      return quizes;
    });

    let tmp = [...answers];
    const itemIndex = tmp.findIndex(t => t.id === ans.id);

    if (itemIndex > -1) tmp[itemIndex] = ans;
    else tmp.push(ans);

    setAnswers(tmp);
  };

  /**
   * Grades the mock exam and reports the payload to the parent.
   *
   * @param {React.FormEvent} e Submit event.
   * @returns {void}
   */
  const onSubmit = (e) => {
    e.preventDefault();

    const answersToSend = answers.filter(ans => Object.keys(ans).length === 3);

    for (let i = 0; i < answersToSend.length; i++) {
      if (!answersToSend[i].id) answersToSend[i].id = `${blockId}_${i + 2}_1`;
    }

    if (!answersToSend.length) return;

    setShowMark(true);
    setIsSubmited(true);

    let rawPossible = tags.filter(t => t.grade).length;
    if (rawPossible < answersToSend.length) rawPossible = answersToSend.length;

    const payload = {
      'student_id': user.id,
      'course_id': courseId,
      'module_id': grade ? grade.module_id : getModuleIdFromCourseId(courseId, blockId),
      'raw_possible': tags.filter(t => t.grade).length,
      'choices': answersToSend
    };
    onSubmitProblem(payload);
  };

  /**
   * Reveals mock-exam hints.
   *
   * @returns {void}
   */
  const onShowHint = () => {
    setShowHint(!isShowHint);
  };

  /**
   * Advances after a mock-exam attempt.
   *
   * @returns {void}
   */
  const onContinueBtnClick = () => {
    onContinueClick();
    setIsSubmited(false);
  };

  return <>
    <form onSubmit={onSubmit}>
      {
        tags.map((t) => {
          const tag = t.tag ? t.tag.tagName : null;
          const tagName = tag ? tag.toLowerCase() : '';
          // const tagName = t.tag.tagName.toLowerCase();
          if (tagName === 'p1' || tagName === 'label') {
            return (
              <PRender tag={t.tag} key={t.key} pkey={t.key} />
            );
          } else if (tagName === 'optionresponse') {
            return (
              <OptionResponseRender // Select box
                key={t.key} pkey={t.key}
                tag={t.tag} grade={t.grade}
                onSetAnswer={onSetAnswer}
                isShowMark={isShowMark}
                setShowMark={setShowMark}
              />
            );

          } else if (tagName === 'multiplechoiceresponse') {
            return (
              <MultipleChoiceResponseRender  // Radio
                key={t.key} pkey={t.key} blockId={blockId}
                tag={t.tag} grade={t.grade}
                onSetAnswer={onSetAnswer}
                isShowMark={isShowMark}
                setShowMark={setShowMark} />
            );
          } else if (tagName === 'choiceresponse') {
            return (
              <ChoiceResponseRender  // Checkbox
                key={t.key} pkey={t.key} blockId={blockId}
                tag={t.tag} grade={t.grade}
                onSetAnswer={onSetAnswer}
                isShowMark={isShowMark}
                setShowMark={setShowMark}
              />
            );
          } else if (tagName === 'numericalresponse') {
            return (
              <NumericalResponseRender
                key={t.key} pkey={t.key}
                tag={t.tag} grade={t.grade}
                onSetAnswer={onSetAnswer}
                isShowMark={isShowMark}
                setShowMark={setShowMark}
              />
            );
          } else if (tagName === 'stringresponse') {
            return (
              <StringResponseRender
                key={t.key} pkey={t.key}
                tag={t.tag} grade={t.grade}
                onSetAnswer={onSetAnswer}
                isShowMark={isShowMark}
                setShowMark={setShowMark}
              />
            );
          } else if (tagName === 'center' || tagName === 'p') {
            return (
              <EquationRender key={t.key} tag={t.tag} />
            );
          } else if (tagName === 'solution') {
            return <SolutionRender tag={t.tag} key={t.key} isShowHint={isShowHint} />;
          }
          return <Fragment key={t.key} />;
        })
      }

      <Divider className="bg-gray-200 dark:bg-gray-500 my-8" />

      <div className="w-full flex mb-3 justify-center mt-4">
        {
          isSubmited ?
            <VButton variant="contained" onClick={onContinueBtnClick} size="large" className="w-40">{t('Continue')}</VButton>
            :
            <VButton variant="contained" type="submit" size="large" className="w-40" onClick={() => { }} disabled={touchedQuizes.some(t => !t.state)}>{t('Submit')}</VButton>
        }

        {
          isContainHint &&
          <VButton onClick={onShowHint} type="button" className="btn v-bg-body bg-main1 text-muted border border-1 p-btn fsp-16 fw-500 lh-22 hover-border transition-all ml-2">
            {isShowHint ? t('Hide hint') : t('Show hint')}
          </VButton>
        }
      </div>
    </form>
  </>;
};

export default ProblemMock;